import type { Dict, Locale } from "./i18n";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  tools?: string[];
}

interface ChatResponse {
  ok: boolean;
  reply?: string;
  tools?: string[];
  error?: string;
}

export function mountChatWidget(host: HTMLElement, dict: Dict, locale: Locale) {
  let open = false;
  let sending = false;
  let history: ChatMessage[] = [];
  let inputValue = "";
  let serverError: string | null = null;

  const c = dict.chat;

  function render() {
    // Hint is always shown while the panel is closed, so reviewers immediately
    // see the AI integration entry point. Hidden as soon as the user opens it.
    const showHint = !open;
    host.innerHTML = `
      ${showHint
        ? `
        <div class="chat-hint" aria-hidden="true">
          <span class="chat-hint__label">${escText(c.hint)}</span>
          <svg class="chat-hint__arrow" width="150" height="93" viewBox="0 0 150 93" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.407928 1.44348C38.7413 12.2768 122.008 45.3435 148.408 90.9435M120.408 84.4435L148.408 90.9435V61.9435" />
          </svg>
        </div>
      `
        : ""}
      <button class="chat-fab ${open ? "chat-fab--open" : ""} ${showHint ? "chat-fab--pulsing" : ""}" type="button" data-act="toggle" aria-label="${escAttr(open ? c.close : c.open)}" aria-expanded="${open ? "true" : "false"}">
        ${open ? "×" : "?"}
      </button>
      ${open
        ? `
        <div class="chat-panel" role="dialog" aria-label="${escAttr(c.title)}">
          <div class="chat-panel__head">
            <strong class="chat-panel__title">${escText(c.title)}</strong>
            <span class="chat-panel__sub">${escText(c.sub)}</span>
          </div>
          <div class="chat-panel__log" id="chat-log">
            ${history.length === 0
              ? `
              <div class="chat-empty">
                <p>${escText(c.greeting)}</p>
                <div class="chat-examples">
                  ${c.examples.map((ex) => `<button class="chat-example" type="button" data-example="${escAttr(ex)}">${escText(ex)}</button>`).join("")}
                </div>
              </div>
              `
              : ""}
            ${history
              .map(
                (m) => `
              <div class="chat-msg chat-msg--${m.role}">
                <div class="chat-msg__bubble">${escText(m.content)}</div>
                ${m.tools && m.tools.length && m.role === "assistant"
                  ? `<div class="chat-msg__meta">${escText(c.toolsUsedPrefix)} ${m.tools.map((t) => `<code>${escText(t)}</code>`).join(", ")}</div>`
                  : ""}
              </div>
            `,
              )
              .join("")}
            ${sending
              ? `<div class="chat-msg chat-msg--assistant"><div class="chat-msg__bubble chat-msg__bubble--typing">${escText(c.thinking)}</div></div>`
              : ""}
            ${serverError ? `<div class="chat-msg chat-msg--error" role="alert">${escText(serverError)}</div>` : ""}
          </div>
          <form class="chat-input" id="chat-form">
            <input
              type="text"
              class="chat-input__field"
              name="q"
              placeholder="${escAttr(c.placeholder)}"
              value="${escAttr(inputValue)}"
              ${sending ? "disabled" : ""}
              autocomplete="off"
              aria-label="${escAttr(c.placeholder)}"
            />
            <button class="chat-input__btn" type="submit" ${sending ? "disabled" : ""} aria-label="${escAttr(c.send)}">↑</button>
          </form>
          <div class="chat-panel__foot">${escText(c.toolNote)}</div>
        </div>
      `
        : ""}
    `;

    host.querySelector<HTMLButtonElement>('[data-act="toggle"]')?.addEventListener("click", () => {
      open = !open;
      serverError = null;
      render();
      if (open) setTimeout(() => host.querySelector<HTMLInputElement>(".chat-input__field")?.focus(), 50);
    });

    if (!open) return;

    const form = host.querySelector<HTMLFormElement>("#chat-form");
    const field = host.querySelector<HTMLInputElement>(".chat-input__field");
    field?.addEventListener("input", () => {
      inputValue = field.value;
    });
    form?.addEventListener("submit", (e) => {
      e.preventDefault();
      send(inputValue);
    });

    host.querySelectorAll<HTMLButtonElement>(".chat-example").forEach((btn) => {
      btn.addEventListener("click", () => {
        send(btn.dataset.example || "");
      });
    });

    scrollToBottom();
  }

  async function send(text: string) {
    const q = text.trim();
    if (!q || sending) return;
    history = [...history, { role: "user", content: q }];
    inputValue = "";
    sending = true;
    serverError = null;
    render();

    try {
      const res = await fetch("/api/ai/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: history.map((m) => ({ role: m.role, content: m.content })),
          locale,
        }),
      });
      const data = (await res.json().catch(() => ({ ok: false, error: "Bad response" }))) as ChatResponse;
      if (!res.ok || !data.ok) {
        serverError = data.error || `Request failed (${res.status})`;
      } else {
        history = [...history, { role: "assistant", content: data.reply || "", tools: data.tools }];
      }
    } catch (err) {
      serverError = err instanceof Error ? err.message : String(err);
    } finally {
      sending = false;
      render();
    }
  }

  function scrollToBottom() {
    const log = host.querySelector<HTMLElement>("#chat-log");
    if (log) log.scrollTop = log.scrollHeight;
  }

  render();
}

function escText(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]!);
}
function escAttr(s: string): string {
  return s.replace(/[&"<>]/g, (c) => ({ "&": "&amp;", '"': "&quot;", "<": "&lt;", ">": "&gt;" })[c]!);
}
